import { Fragment, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { endOfDay, startOfDay } from 'date-fns';
import { CSVLink, Flex, StyledDatePicker, StyledFormSelect, WrappedQuery } from '../';
import {
  GRANULARITY,
  ACTIVITY_STATS_LEVELS,
  activityStatsVisibleOptions,
  activityStatsSelectOptions,
} from '../../../consts';
import {
  ALL_INTERVENTIONS,
  ALL_SERVICE_PROVIDERS,
  ALL_STAKEHOLDER_TYPES,
  INSTITUTIONS_FILTERED,
  EXPORT_ACTIVITIES,
} from '../../../graphql';
import { formatActivityStatsData, formatSelectValue, getPortal, sanitizeData } from '../../../utils';
import { getAllProgrammesQuery } from '../../../containers/shared/programmes/utils';

type State = {
  granularity?: string;
  level?: string;
  programmeIds?: string[];
  serviceProviderIds?: string[];
  interventionIds?: string[];
  institutionIds?: string[];
  stakeholderTypeName?: string;
};

type Props = {
  serviceProvider: string;
  institution: string;
  intervention: string;
  stakeholderType: string;
  startDate: Date;
  endDate: Date;
  programme: string;
  step: string;
};

const ActivitiesStatExport = ({
  programme,
  stakeholderType,
  institution,
  intervention,
  serviceProvider,
  step,
}: Props) => {
  const [portal] = useState(getPortal());
  const [state, setState] = useState<State>({});
  const [institutions, setInstitutions] = useState<[{ node: { id: string } }] | any>([]);
  const [levelList, setLevelList] = useState<any>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [headers, setHeaders] = useState<{ label: string; key: string }[] | undefined>(undefined);

  const { data: programmeData, error: programmeError, loading: programmeDataLoading } = useQuery(
    getAllProgrammesQuery(portal),
    {
      variables: {
        input: {
          isArchived: false,
          includeWhereReporter: true,
        },
      },
    }
  );
  const { data: spData, error: spError, loading: spDataLoading } = useQuery(ALL_SERVICE_PROVIDERS, {
    variables: {
      input: {
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
      },
    },
  });
  const { data: interventionData, error: interventionError, loading: interventionDataLoading } = useQuery(
    ALL_INTERVENTIONS,
    {
      variables: {
        input: {
          ...(state.programmeIds && { programmeIds: state.programmeIds }),
          ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
        },
      },
    }
  );
  const {
    data: institutionData,
    fetchMore: fetchMoreInstitutions,
    error: institutionError,
    loading: institutionLoading,
  } = useQuery(INSTITUTIONS_FILTERED, {
    variables: {
      input: {
        first: 10,
        ...(state.programmeIds && { programmeIds: state.programmeIds }),
        ...(state.interventionIds && { interventionIds: state.interventionIds }),
      },
    },
  });
  const { data: stakeholderTypeData, error: stakeholderTypeError, loading: stakeholderTypeLoading } = useQuery(
    ALL_STAKEHOLDER_TYPES
  );

  useEffect(() => {
    if (institutionData && institutionData.institutions && institutionData.institutions.edges) {
      setInstitutions(institutionData.institutions.edges);
    }
  }, [institutionData]);

  useEffect(() => {
    const { granularity, level, stakeholderTypeName } = state;

    if (level) {
      setLevelList(activityStatsVisibleOptions[level]);
    }

    if (granularity && level && level !== 'stakeholder') {
      setHeaders(activityStatsSelectOptions[granularity][level]);
    }

    if (granularity && level && level === 'stakeholder' && stakeholderTypeName) {
      setHeaders(activityStatsSelectOptions[granularity][level][stakeholderTypeName]);
    }
  }, [state]);

  return (
    <Flex size="1" direction="column">
      <Fragment>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isClearable={true}
            name="granularity"
            label="Granularity"
            onChange={(option: any) => {
              const newState = Object.assign({}, state, {
                granularity: option ? option.value : undefined,
              });
              setState(newState);
            }}
            options={GRANULARITY}
          />
        </Flex>
        <Flex size="1" direction="row" marginRight="10px">
          <StyledFormSelect
            isClearable={true}
            name="level"
            label="Level"
            onChange={(option: any) => {
              const newState = Object.assign({}, state, {
                level: option ? option.value : undefined,
              });
              setState(newState);
            }}
            options={ACTIVITY_STATS_LEVELS}
          />
        </Flex>
      </Fragment>

      <Fragment>
        {levelList.includes('programme') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="programmeIds"
              label="Programme"
              placeholder={programmeDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={programmeDataLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [programme]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                programmeDataLoading || programmeError || !(programmeData && programmeData.programmes)
                  ? []
                  : programmeData.programmes.map((item: any) => formatSelectValue({ label: item.name, value: item.id }))
              }
            />
          </Flex>
        )}

        {levelList.includes('serviceProvider') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="serviceProviderIds"
              label="Service Provider"
              placeholder={spDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={spDataLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [serviceProvider]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                spDataLoading || spError || !(spData && spData.serviceProviders)
                  ? []
                  : spData.serviceProviders.map((item: any) => formatSelectValue({ label: item.name, value: item.id }))
              }
            />
          </Flex>
        )}

        {levelList.includes('intervention') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="interventionIds"
              label="Intervention"
              placeholder={interventionDataLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={interventionDataLoading}
              onChange={(option: any[]) => {
                const newState = Object.assign({}, state, {
                  [intervention]: option ? option.map(opt => opt.value) : undefined,
                });
                setState(newState);
              }}
              options={
                interventionDataLoading || interventionError || !(interventionData && interventionData.allInterventions)
                  ? []
                  : interventionData.allInterventions.map((item: any) =>
                      formatSelectValue({ label: item.name, value: item.id })
                    )
              }
            />
          </Flex>
        )}

        {levelList.includes('institution') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={true}
              isClearable={true}
              name="institutionIds"
              label="Institution"
              placeholder={institutionLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={institutionLoading}
              onChange={(option: any[]) => {
                if (!option) {
                  setState({ [institution]: undefined });
                } else {
                  const newState = Object.assign({}, state, { [institution]: option.map(opt => opt.value) });
                  setState(newState);
                }
              }}
              options={
                institutionLoading || institutionError || !Boolean(institutions.length)
                  ? []
                  : institutions.map((item: any) =>
                      formatSelectValue({
                        label: item.node.name,
                        value: item.node.id,
                      })
                    )
              }
              scrollHandler={async () => {
                // call the next page of the filteredInsitutions query
                return (
                  institutionData.institutions &&
                  institutionData.institutions.pageInfo &&
                  institutionData.institutions.pageInfo.hasNextPage &&
                  fetchMoreInstitutions({
                    variables: {
                      input: {
                        first: 10,
                        skip: institutions.length,
                        ...(state.programmeIds && { programmeIds: state.programmeIds }),
                        ...(state.interventionIds && { interventionIds: state.interventionIds }),
                      },
                    },
                  }).then(({ data }: any) => {
                    if (data && data.institutions && data.institutions.edges) {
                      const newInstitutions = [...institutions, ...data.institutions.edges];
                      setInstitutions(newInstitutions);
                    }
                  })
                );
              }}
            />
          </Flex>
        )}

        {levelList.includes('stakeholder') && (
          <Flex size="1" direction="row" marginRight="10px">
            <StyledFormSelect
              isMulti={false}
              isClearable={true}
              name="stakeholderType"
              label="Stakeholder Type"
              placeholder={stakeholderTypeLoading ? 'Data loading, Please wait' : 'Data ready, Please select option'}
              isDisabled={stakeholderTypeLoading}
              onChange={(option: any) => {
                const newState = Object.assign({}, state, {
                  [stakeholderType]: option && option.value,
                  stakeholderTypeName: option && option.label,
                });
                setState(newState);
              }}
              options={
                stakeholderTypeLoading ||
                stakeholderTypeError ||
                !(stakeholderTypeData && stakeholderTypeData.stakeholderTypes)
                  ? []
                  : stakeholderTypeData.stakeholderTypes.map((item: any) =>
                      formatSelectValue({ label: item.name, value: item.id })
                    )
              }
            />
          </Flex>
        )}

        <Flex size="1" direction="row" marginRight="10px">
          <StyledDatePicker
            name="activityDates"
            label="Dates"
            startDate={startDate || undefined}
            endDate={endDate || undefined}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
          />
        </Flex>
      </Fragment>

      {state.granularity && state.level && (
        <WrappedQuery
          query={EXPORT_ACTIVITIES}
          variables={{
            input: {
              first: 10,
              ...(state.granularity && { granularity: state.granularity }),
              ...(state.level && { level: state.level }),
              ...(state.stakeholderTypeName && { stakeholderType: state.stakeholderTypeName }),
              ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
              ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
              ...(state.programmeIds && { programmeIds: state.programmeIds }),
              ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
              ...(state.interventionIds && { interventionIds: state.interventionIds }),
              ...(state.institutionIds && { schoolIds: state.institutionIds }),
            },
          }}
          overrideStates={true}
        >
          {({ loading, error, data, fetchMore }) => {
            if (loading) {
              return <div>Preparing data for export...</div>;
            }

            if (error) {
              return <div>Something went wrong</div>;
            }

            // call the next page of the exportActivities query
            data.activities &&
              data.activities.pageInfo &&
              data.activities.pageInfo.hasNextPage &&
              fetchMore({
                variables: {
                  input: {
                    first: 10,
                    ...(state.granularity && { granularity: state.granularity }),
                    ...(state.level && { level: state.level }),
                    ...(state.stakeholderTypeName && { stakeholderType: state.stakeholderTypeName }),
                    ...(startDate && { startDate: startOfDay(new Date(startDate)) }),
                    ...(endDate && { endDate: endOfDay(new Date(endDate)) }),
                    ...(state.programmeIds && { programmeIds: state.programmeIds }),
                    ...(state.serviceProviderIds && { serviceProviderIds: state.serviceProviderIds }),
                    ...(state.interventionIds && { interventionIds: state.interventionIds }),
                    ...(state.institutionIds && { schoolIds: state.institutionIds }),
                    after: data.activities && data.activities.pageInfo && data.activities.pageInfo.endCursor,
                  },
                },
                updateQuery: (previousResult, { fetchMoreResult }) => {
                  const newActivities = fetchMoreResult.activities.edges;
                  return {
                    activities: {
                      aggregate: fetchMoreResult.activities.aggregate,
                      pageInfo: fetchMoreResult.activities.pageInfo,
                      edges: [...newActivities, ...previousResult.activities.edges],
                      __typename: fetchMoreResult.activities.__typename,
                    },
                  };
                },
              });

            if (data.activities && data.activities.pageInfo && data.activities.pageInfo.hasNextPage) {
              return <div>Fetching more pages...</div>;
            }

            const { granularity, level, institutionIds, stakeholderTypeName } = state;
            const formatted: any =
              data && data.activities && granularity && level
                ? formatActivityStatsData({
                    granularity,
                    level,
                    data: data.activities.edges,
                    schoolIds: institutionIds,
                    stakeholderTypeName,
                    startDate,
                    endDate,
                  })
                : [];

            const sanitized = formatted.length ? sanitizeData(formatted) : [];
            return (
              <Fragment>
                <Flex size="1" direction="row" marginRight="10px">
                  <Flex size="0.2" direction="row" marginRight="10px">
                    {sanitized && sanitized.length > 0 && (
                      <CSVLink filename={'ActivitiesStatExport.csv'} headers={headers} data={sanitized}>
                        Export CSV
                      </CSVLink>
                    )}

                    {sanitized && sanitized.length < 1 && <span>No activities data for given parameters</span>}
                  </Flex>
                </Flex>
              </Fragment>
            );
          }}
        </WrappedQuery>
      )}
      {!(state.granularity && state.level) && <span>No activities data for given parameters</span>}
    </Flex>
  );
};

export default ActivitiesStatExport;
